import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const Skills = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: (i = 0) => ({
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, delay: i * 0.1, ease: [0.25, 1, 0.5, 1] },
    }),
  };

  return (
    <section
      id="skills"
      className="relative py-10 md:py-10 overflow-hidden bg-darker text-white"
    >
      {/* Background Atmosphere */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-[#0a0505] to-black"></div>
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] rounded-full opacity-10 blur-3xl bg-primary"></div>
        <div className="absolute bottom-1/4 left-0 w-[400px] h-[400px] rounded-full opacity-5 blur-3xl bg-white"></div>
      </div>

      {/* Checkered pattern top-left */}
      <div className="absolute top-8 left-8 grid grid-cols-6 gap-[2px] opacity-20 pointer-events-none z-10">
        {[...Array(24)].map((_, i) => (
          <div
            key={i}
            className={`w-3 h-3 ${
              (Math.floor(i / 6) + i) % 2 === 0 ? 'bg-white' : 'bg-transparent'
            }`}
          ></div>
        ))}
      </div>

      <div ref={ref} className="relative max-w-5xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="mb-10 md:mb-14">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="flex items-center gap-3 mb-4"
          >
            <div className="w-8 md:w-12 h-[2px] bg-primary"></div>
            <span className="text-primary font-mono text-[10px] md:text-xs tracking-[0.3em] uppercase">
              The Journey
            </span>
          </motion.div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <motion.h2
              variants={fadeUp}
              custom={1}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              className="font-heading text-4xl md:text-6xl lg:text-7xl text-white leading-[0.9] tracking-tight font-black"
            >
              MY <span className="text-primary">STORY</span>
            </motion.h2>

            <motion.p
              variants={fadeUp}
              custom={2}
              initial="hidden"
              animate={isInView ? 'visible' : 'hidden'}
              className="text-sm md:text-base text-white/50 max-w-md leading-relaxed"
            >
              Two decades of dirt, desert, and tarmac. Watch the story unfold —
              the moments, the machines, the mindset behind the ride.
            </motion.p>
          </div>
        </div>

        {/* Video & Quote Layout */}
        <div className="space-y-6">
          {/* YouTube Video Card */}
          <motion.div
            variants={fadeUp}
            custom={3}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="relative bg-gradient-to-br from-[#0a0a0a] via-[#141414] to-[#0a0a0a] rounded-2xl md:rounded-3xl border border-white/10 overflow-hidden shadow-2xl"
          >
            {/* Clean Header with YouTube SVG */}
            <div className="px-5 py-3.5 border-b border-white/10 flex items-center justify-between bg-black/50">
              <div className="flex items-center gap-3">
                {/* YouTube Icon SVG */}
                <svg
                  className="w-6 h-6 text-red-600 fill-current shrink-0"
                  viewBox="0 0 24 24"
                >
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                </svg>
                <span className="font-heading text-base md:text-lg text-white font-bold tracking-wide">
                  The Vishwas Story
                </span>
              </div>

              <span className="text-[10px] md:text-xs font-mono text-white/40 tracking-widest uppercase">
                Official Feature
              </span>
            </div>

            {/* Responsive YouTube Video Frame */}
            <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/4z7LrhIUHuk?si=hX9zBLp7OnTZJQGR"
                title="Vishwas — My Story"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              ></iframe>
            </div>
          </motion.div>

          {/* Sleek "Milestones That Matter" Block */}
          <motion.div
            variants={fadeUp}
            custom={4}
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            className="p-6 md:p-8 rounded-2xl md:rounded-3xl bg-gradient-to-br from-zinc-900/80 via-black to-zinc-900/50 border border-white/10 relative overflow-hidden backdrop-blur-md"
          >
            <div className="absolute top-0 left-0 w-1.5 h-full bg-primary"></div>

            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] md:text-xs font-mono tracking-[0.3em] text-primary uppercase font-bold flex items-center gap-2">
                <span className="w-2 h-2 bg-primary inline-block"></span>
                Milestones That Matter
              </span>
            </div>

            <blockquote className="my-2">
              <p className="font-heading text-xl md:text-3xl text-white tracking-wide italic leading-snug">
                “Every scar, every trophy — same story.”
              </p>
            </blockquote>

            <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between">
              <span className="font-mono text-sm md:text-base text-primary font-bold tracking-widest uppercase">
                — VISHWAS
              </span>
            </div>
          </motion.div>
        </div>

        {/* Bottom Tag */}
        <motion.div
          variants={fadeUp}
          custom={5}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mt-12 md:mt-16 flex items-center justify-center gap-3"
        >
          <div className="w-8 h-[1px] bg-white/20"></div>
          <span className="text-[10px] md:text-xs tracking-[0.3em] text-white/40 uppercase font-mono">
            The ride continues
          </span>
          <div className="w-8 h-[1px] bg-white/20"></div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;